import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import { formSnapshot } from "../../../../../../../shared/frontend/forms/formSnapshot";
import { App } from "../../../../../../../shared/frontend/ui";
import { errorMessage } from "../../../app/utils/errorMessage";
import { DeleteOutlined } from "@ant-design/icons";
import { Button,Form,Input,InputNumber,Select,Space,Checkbox } from "../../../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import React from "react";
import { CYCLE_UNIT_ONE_TIME } from "../../../app/constants/cycleUnit";
import type { UnitsSettings } from "../../../app/units/units";
import { PAYMENT_METHOD_SEPA } from "../../../app/utils/paymentMethodCodes";
import { FormDrawer } from "../../../components/FormDrawer";
import { ResponsiveFormGrid } from "../../../components/ui/antd";
import type { PersonServiceRow } from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import { ACCESS_SCOPE_ANY_STUDIO } from "../../../app/constants/accessScope";

const STRINGIFIED_NULL = "null";
const STRINGIFIED_UNDEFINED = "undefined";

type SelectOption = {
  value: string | number;
  label: React.ReactNode;
};

type SepaGroup = {
  id: string;
  name: string;
  isActive: boolean;
};

type PersonServiceEditDrawerProps = {
  personId: string;
  open: boolean;
  serviceEdit: PersonServiceRow | null;
  form: FormInstance;
  isLocked: boolean;
  setIsLocked: (isLocked: boolean) => void;
  sepaGroups: SepaGroup[];
  paymentMethodSelectOptions: SelectOption[];
  cycleUnitSelectOptions: SelectOption[];
  dateInputFormat: string | string[];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  units: UnitsSettings;
  api: {
    put: <T = unknown>(url: string, body?: unknown) => Promise<T>;
    del: <T = unknown>(url: string) => Promise<T>;
  };
  modal: {
    confirm: ReturnType<typeof App.useApp>["modal"]["confirm"];
  };
  message: {
    success: (content: React.ReactNode) => void;
    error: (content: React.ReactNode) => void;
  };
  text: (key: string) => string;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted: () => void;
};

export function PersonServiceEditDrawer({
  personId,
  open,
  serviceEdit,
  form,
  isLocked,
  setIsLocked,
  sepaGroups,
  paymentMethodSelectOptions,
  cycleUnitSelectOptions,
  dateInputFormat,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  api,
  modal,
  message,
  text,
  onCancel,
  onSaved,
  onDeleted
}: PersonServiceEditDrawerProps) {
  const operation = useFinanceOperation(personId);
  const serviceFields = Form.useWatch(formSnapshot, { form: form, preserve: true });
  const { dirty: serviceDirty, markSaved: serviceMarkSaved } = useDirtySnapshot({ fields: serviceFields, extra: { isLocked } });
  const serviceInitializeDirty = useDrawerBaselineEvent(() => serviceMarkSaved({ fields: formSnapshot(form.getFieldsValue(true)), extra: { isLocked } }));
  useDrawerBaselineEffect(() => { if (open) serviceInitializeDirty(); }, [open]);

  const handleDelete = React.useCallback(async () => {
    if (!serviceEdit) return;
    const contractName = String(serviceEdit.name || "").trim() || text("Unnamed sub-contract");
    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Delete sub-contract"),
        content: text("Delete sub-contract confirm").replace("{0}", contractName),
        okText: text("Delete"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;
    await operation.execute(async (committed) => {
      await api.del(`/api/v1/contracts/${encodeURIComponent(String(serviceEdit.id))}`);
      committed();
      message.success(text("Deleted."));
      onDeleted();
    });
  }, [api, message, modal, onDeleted, serviceEdit, text, operation]);

  const handleSave = React.useCallback(async () => {
    if (!serviceEdit) {
      return;
    }

    try {
      const v = await form.validateFields();

      const startIso = formatDateOnlyIso(v.startDate);
      const endIso = formatDateOnlyIso(v.endDate);
      if (!startIso) {
        throw new Error(text("Start is required."));
      }

      const nextIsLocked = isLocked === true;

      // WICHTIG: Lese sepaGroupId direkt aus dem Formular
      // Der Wert muss korrekt gelesen werden, auch wenn das Feld nicht sichtbar ist
      const sepaGroupIdFromValidate = v.sepaGroupId;
      const sepaGroupIdFromForm = form.getFieldValue("sepaGroupId");
      let sepaGroupIdValue = sepaGroupIdFromValidate ?? sepaGroupIdFromForm;
      const paymentMethodValue = v.paymentMethod != null ? Number(v.paymentMethod) : null;

      // WICHTIG: Wenn Zahlungsmethode SEPA ist, muss eine Lastschriftgruppe ausgewählt sein
      // Wenn keine ausgewählt ist, verwende die erste verfügbare als Fallback
      if (paymentMethodValue === PAYMENT_METHOD_SEPA) {
        if (!sepaGroupIdValue || sepaGroupIdValue === "" || String(sepaGroupIdValue) === STRINGIFIED_NULL || String(sepaGroupIdValue) === STRINGIFIED_UNDEFINED) {
          // Keine Lastschriftgruppe ausgewählt - verwende die erste verfügbare als Fallback
          const activeSepaGroups = sepaGroups.filter(g => g.isActive);
          if (activeSepaGroups.length > 0) {
            const firstActiveSepaGroup = activeSepaGroups[0];
            sepaGroupIdValue = firstActiveSepaGroup.id;
            form.setFieldValue("sepaGroupId", firstActiveSepaGroup.id);
          } else {
            throw new Error(text("No active SEPA group available. Please create a SEPA group."));
          }
        }
      }

      // Bestimme den zu sendenden Wert
      let sepaGroupIdToSend: string | null = null;
      if (paymentMethodValue === PAYMENT_METHOD_SEPA) {
        // Zahlungsmethode ist SEPA - verwende den Wert aus dem Formular
        // WICHTIG: Der Wert muss jetzt gesetzt sein (durch Validierung/Fallback oben)
        if (sepaGroupIdValue != null && sepaGroupIdValue !== "" && String(sepaGroupIdValue) !== STRINGIFIED_NULL && String(sepaGroupIdValue) !== STRINGIFIED_UNDEFINED) {
          sepaGroupIdToSend = String(sepaGroupIdValue);
        } else {
          throw new Error(text("A SEPA group must be selected when payment method is SEPA."));
        }
      }
      // Wenn Zahlungsmethode nicht SEPA ist, bleibt sepaGroupIdToSend = null

      try {
        const requestBody = {
          name: String(v.name || serviceEdit.name || "").trim(),
          startDate: startIso,
          endDate: endIso || null,
          value: v.value != null ? Number(v.value) : null,
          usageLimitValue: serviceEdit.usageLimitValue ?? null,
          usageLimitUnit: serviceEdit.usageLimitUnit ?? null,
          paymentCycleValue: v.paymentCycleValue != null && Number(v.paymentCycleUnit) !== CYCLE_UNIT_ONE_TIME ? Number(v.paymentCycleValue) : null,
          paymentCycleUnit: v.paymentCycleUnit != null ? Number(v.paymentCycleUnit) : null,
          durationValue: serviceEdit.durationValue ?? null,
          durationUnit: serviceEdit.durationUnit ?? null,
          renewalValue: serviceEdit.renewalValue ?? null,
          renewalUnit: serviceEdit.renewalUnit ?? null,
          allowsAccess: serviceEdit.allowsAccess !== false,
          homeStudioId: serviceEdit.homeStudioId ?? null,
          accessScope: Number(serviceEdit.accessScope || ACCESS_SCOPE_ANY_STUDIO),
          allowedStudioIds: (serviceEdit.allowedStudioIds || []).map(String),
          requiresNoArrears: serviceEdit.requiresNoArrears !== false,
          isActive: !nextIsLocked,
          isLocked: nextIsLocked,
          paymentMethod: paymentMethodValue,
          sepaGroupId: sepaGroupIdToSend
        };

        await operation.execute(async (committed) => {
          await api.put(`/api/v1/contracts/${encodeURIComponent(String(serviceEdit.id))}`, requestBody);
          committed();
          message.success(text("Saved."));
          onSaved();
        });
      } catch (e: unknown) {
        const errorMsg = errorMessage(e, String(e));
        // Übersetze bekannte Fehlermeldungen
        let displayMsg = errorMsg;
        if (errorMsg.includes("Name fehlt") || errorMsg.includes("Name field is required")) {
          displayMsg = text("Name is required");
        } else if (errorMsg.includes("not found") || errorMsg.includes("NotFound")) {
          displayMsg = text("Contract not found");
        } else if (errorMsg.includes("Name: The Name field is required")) {
          displayMsg = text("Name: The Name field is required");
        }
        message.error(displayMsg);
      }
    } catch {
      // Central API toast shows error
    }
  }, [api, form, isLocked, message, onSaved, sepaGroups, serviceEdit, text, operation]);

  return (
    <FormDrawer mode="edit" dirty={serviceDirty}
      title="Edit contract block"
      open={open}
      onCancel={onCancel}
      submitting={operation.pending}
      extra={
        <>
          <Checkbox checked={isLocked === true} onChange={({ target: { checked: val } }) => setIsLocked(val)}>{text("locked")}</Checkbox>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={!serviceEdit}
            onClick={() => { return handleDelete().catch(() => {}); }}
          >
            {text("Delete")}
          </Button>
        </>
      }
      onSave={handleSave}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label={text("Name")}
          rules={[{ required: true, message: text("Name is required") }]}
        >
          <Input />
        </Form.Item>

        <ResponsiveFormGrid>
          <Form.Item
            name="startDate"
            label={text("Start")}
            rules={[{ required: true, message: text("Start is required") }]}
          >
            <DatePicker allowClear={false} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endDate" label={text("End")}>
            <DatePicker allowClear placeholder={text("—")} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
        </ResponsiveFormGrid>

        <Form.Item name="value" label={text("Price")}>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={0.01}
            decimalSeparator={decimalSeparator}
            addonAfter={currencyCode}
            placeholder={moneyPlaceholder}
          />
        </Form.Item>

        <Form.Item label={text("Payment cycle")}>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item name="paymentCycleValue" noStyle>
              <InputNumber
                min={1}
                step={1}
                precision={0}
                style={{ width: "40%" }}
                placeholder="1"
                disabled={Number(form.getFieldValue("paymentCycleUnit")) === CYCLE_UNIT_ONE_TIME}
              />
            </Form.Item>
            <Form.Item name="paymentCycleUnit" noStyle>
              <Select
                style={{ width: "60%" }}
                options={cycleUnitSelectOptions}
                allowClear
                placeholder={text("—")}
                onChange={(v) => {
                  if (v == null || v === CYCLE_UNIT_ONE_TIME) form.setFieldsValue({ paymentCycleValue: null });
                }}
              />
            </Form.Item>
          </Space.Compact>
        </Form.Item>

        <Form.Item name="paymentMethod" hidden>
          <Select style={{ width: "100%" }} options={paymentMethodSelectOptions} allowClear placeholder={text("—")} />
        </Form.Item>

        {/* WICHTIG: SepaGroupId-Feld immer im Formular behalten, auch wenn nicht sichtbar */}
        <Form.Item shouldUpdate noStyle>
          {() => {
            const paymentMethod = form.getFieldValue("paymentMethod");
            const isSepa = Number(paymentMethod) === PAYMENT_METHOD_SEPA;
            // Zeige Lastschriftgruppe nur an, wenn Zahlungsmethode SEPA ist
            // ABER: Das Formular-Feld muss immer existieren, auch wenn nicht sichtbar
            return (
              <Form.Item
                name="sepaGroupId"
                label={text("SEPA group")}
                hidden={!isSepa}
                rules={isSepa ? [
                  {
                    required: true,
                    message: text("A SEPA group must be selected when payment method is SEPA.")
                  }
                ] : []}
              >
                <Select
                  allowClear={!isSepa}
                  placeholder={sepaGroups.length === 0 ? text("No groups available") : (isSepa ? text("Please select") : text("None"))}
                  options={sepaGroups.filter(g => g.isActive).map((g) => ({ value: g.id, label: g.name }))}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </FormDrawer>
  );
}
